import React, { useState, useEffect } from 'react';
import { CountriesAPI } from '../api';

export default function PhoneInput({ 
  value = '', 
  onChange, 
  label = 'Phone Number',
  required = false,
  className = '',
  placeholder = 'Enter phone number'
}) {
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(true);

  // Load countries on component mount
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const token = localStorage.getItem('token');
        const countriesList = await CountriesAPI.list(token);
        const activeCountries = countriesList.filter(country => country.active);
        setCountries(activeCountries);
        
        // Try to parse existing value to set country and phone
        if (value) {
          parseExistingValue(value, activeCountries);
        }
      } catch (error) {
        console.error('Failed to load countries:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCountries();
  }, []);

  // Parse existing phone value to extract country code and number
  const parseExistingValue = (fullValue, countriesList) => {
    for (const country of countriesList) {
      if (fullValue.startsWith(country.phoneCode)) {
        setSelectedCountry(country);
        setPhoneNumber(fullValue.replace(country.phoneCode, ''));
        return;
      }
    }
    // If no country code found, treat as phone number only
    setPhoneNumber(fullValue);
  };

  // Handle country selection
  const handleCountryChange = (countryId) => {
    const country = countries.find(c => c._id === countryId);
    setSelectedCountry(country);
    
    // Clear phone number when country is deselected
    if (!country) {
      setPhoneNumber('');
      onChange('');
    } else {
      // Update the full phone value with existing phone number
      const fullValue = `${country.phoneCode}${phoneNumber}`;
      onChange(fullValue);
    }
  };

  // Handle phone number input
  const handlePhoneChange = (e) => {
    // Don't allow changes if no country is selected
    if (!selectedCountry) return;
    
    let inputValue = e.target.value;
    
    // Only allow digits
    inputValue = inputValue.replace(/\D/g, '');
    
    // Limit length based on selected country
    if (inputValue.length > selectedCountry.phoneLength) {
      inputValue = inputValue.slice(0, selectedCountry.phoneLength);
    }
    
    setPhoneNumber(inputValue);
    
    // Update the full phone value
    const fullValue = `${selectedCountry.phoneCode}${inputValue}`;
    onChange(fullValue);
  };

  // Handle external value changes
  useEffect(() => {
    if (value !== `${selectedCountry?.phoneCode || ''}${phoneNumber}`) {
      parseExistingValue(value, countries);
    }
  }, [value]);

  if (loading) {
    return (
      <div className={className}>
        <label className="text-xs text-slate-600">
          {label} {required && '*'}
        </label>
        <div className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 bg-slate-50">
          Loading countries...
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <label className="text-xs text-slate-600">
        {label} {required && '*'}
      </label>
      <div className="mt-1 flex gap-2">
        {/* Country Code Selector */}
        <select
          value={selectedCountry?._id || ''}
          onChange={(e) => handleCountryChange(e.target.value)}
          className="w-36 rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 hover:border-slate-300 transition-colors"
          required={required}
        >
          <option value="">Select Country</option>
          {countries.map(country => (
            <option key={country._id} value={country._id}>
              {country.phoneCode} ({country.code})
            </option>
          ))}
        </select>

        {/* Phone Number Input */}
        <input
          type="tel"
          value={phoneNumber}
          onChange={handlePhoneChange}
          placeholder={selectedCountry ? `Enter ${selectedCountry.phoneLength} digits` : "Select country first"}
          maxLength={selectedCountry?.phoneLength || 20}
          disabled={!selectedCountry}
          className={`flex-1 rounded-xl border px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-colors ${
            selectedCountry 
              ? 'border-slate-200 bg-white hover:border-slate-300' 
              : 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
          }`}
          required={required}
        />
      </div>
      
      {/* Helper text */}
      <div className="mt-2">
        {selectedCountry ? (
          <div className="text-xs text-slate-600 bg-slate-50 px-2 py-1 rounded-lg border border-slate-200">
            <span className="font-medium">{selectedCountry.name}</span> - {selectedCountry.phoneLength} digits required
          </div>
        ) : (
          <div className="text-xs text-slate-500 bg-amber-50 px-2 py-1 rounded-lg border border-amber-200">
            Please select a country first
          </div>
        )}
      </div>
    </div>
  );
}
